import { useEffect, useRef, useState } from "react";
import { ScanFace } from "lucide-react";

const WASM = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm";
const MODEL = "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
const GUIDE = {
  loading: "Menyiapkan kamera...",
  center: "Posisikan wajah di dalam lingkaran",
  right: "Geleng ke KANAN",
  left: "Geleng ke KIRI",
  done: "Wajah terverifikasi",
};

let landmarkerPromise = null;
const getLandmarker = () => {
  if (!landmarkerPromise) {
    landmarkerPromise = import("@mediapipe/tasks-vision").then(async ({ FaceLandmarker, FilesetResolver }) => {
      const fs = await FilesetResolver.forVisionTasks(WASM);
      return FaceLandmarker.createFromOptions(fs, { baseOptions: { modelAssetPath: MODEL, delegate: "GPU" }, runningMode: "VIDEO", numFaces: 1 });
    }).catch((e) => { landmarkerPromise = null; throw e; });
  }
  return landmarkerPromise;
};

const yawRatio = (lm) => (lm[1].x - lm[234].x) / ((lm[454].x - lm[234].x) || 1);
const inCircle = (lm) => {
  const xs = lm.map((p) => p.x);
  const ys = lm.map((p) => p.y);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  const w = Math.max(...xs) - Math.min(...xs);
  return Math.abs(cx - 0.5) < 0.14 && Math.abs(cy - 0.5) < 0.16 && w > 0.28 && w < 0.75;
};

const useFaceLoop = (videoRef, canvasRef, active, onStep) => {
  useEffect(() => {
    if (!active) return undefined;
    let raf = 0;
    let stop = false;
    let step = "center";
    let firstSide = null;
    let stable = 0;
    getLandmarker().then((lmk) => {
      onStep("center");
      const loop = () => {
        if (stop) return;
        const v = videoRef.current;
        const c = canvasRef.current;
        if (v && c && v.readyState >= 2) {
          const res = lmk.detectForVideo(v, performance.now());
          const ctx = c.getContext("2d");
          c.width = c.clientWidth; c.height = c.clientHeight;
          ctx.clearRect(0, 0, c.width, c.height);
          const lm = res.faceLandmarks?.[0];
          if (lm) {
            ctx.fillStyle = "rgba(52,211,153,0.9)";
            for (let i = 0; i < lm.length; i += 3) { ctx.beginPath(); ctx.arc((1 - lm[i].x) * c.width, lm[i].y * c.height, 1.6, 0, 6.3); ctx.fill(); }
            const r = yawRatio(lm);
            if (step === "center") {
              stable = inCircle(lm) && r > 0.38 && r < 0.62 ? stable + 1 : 0;
              if (stable > 12) { step = "right"; onStep(step); }
            } else if (step === "right" && (r < 0.3 || r > 0.7)) {
              firstSide = r < 0.5 ? "lo" : "hi"; step = "left"; onStep(step);
            } else if (step === "left" && ((firstSide === "lo" && r > 0.7) || (firstSide === "hi" && r < 0.3))) {
              step = "back";
            } else if (step === "back" && r > 0.4 && r < 0.6 && inCircle(lm)) {
              step = "done"; onStep("done"); return;
            }
          } else if (step === "center") stable = 0;
        }
        raf = requestAnimationFrame(loop);
      };
      loop();
    }).catch(() => onStep("error"));
    return () => { stop = true; cancelAnimationFrame(raf); };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const FaceScan = ({ onCapture, busy }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [err, setErr] = useState("");
  const [step, setStep] = useState("loading");
  useEffect(() => {
    let s = null;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } }, audio: false })
      .then((st) => { s = st; setStream(st); })
      .catch(() => setErr("Izin kamera ditolak. Aktifkan kamera depan untuk absensi."));
    return () => s?.getTracks().forEach((t) => t.stop());
  }, []);
  useEffect(() => { if (videoRef.current && stream) videoRef.current.srcObject = stream; }, [stream]);
  useFaceLoop(videoRef, canvasRef, Boolean(stream), (s) => {
    setStep(s);
    if (s !== "done") return;
    const v = videoRef.current;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => onCapture(b), "image/jpeg", 0.9);
  });
  const text = err || (step === "error" ? "Gagal memuat pemindai wajah. Periksa koneksi lalu muat ulang." : busy ? "Memverifikasi wajah..." : GUIDE[step] || GUIDE.left);
  const ok = step === "done";
  return (
    <div className="w-full" data-testid="face-scan">
      <div className="flex items-center justify-center gap-2 mb-4">
        <ScanFace className="h-7 w-7 text-primary" />
        <p className="font-heading text-xl font-bold tracking-tight" data-testid="face-login-title">Face Login</p>
      </div>
      <div className="relative aspect-square w-full rounded-3xl overflow-hidden bg-black">
        {stream && <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover -scale-x-100" data-testid="face-video" />}
        <canvas ref={canvasRef} className="absolute inset-0 h-full w-full pointer-events-none" />
        <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[68%] aspect-[3/4] rounded-[50%] border-[3px] ${ok ? "border-emerald-400" : "border-white/80"} border-dashed pointer-events-none transition-colors`} />
        {!ok && stream && <div className="absolute inset-x-[16%] h-0.5 bg-emerald-400/80 shadow-[0_0_12px_2px_rgba(52,211,153,.8)] animate-[scanline_2.2s_ease-in-out_infinite]" />}
        <div className="absolute inset-x-3 bottom-3 rounded-2xl bg-black/60 backdrop-blur px-4 py-3 text-center">
          <p className={`text-lg font-bold leading-snug ${ok ? "text-emerald-300" : "text-white"}`} data-testid="face-guide">{text}</p>
        </div>
      </div>
    </div>
  );
};
